import { useEffect, useCallback, Suspense } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useBaaSAuth } from '@/contexts/BaaSAuthContext';
import { Banknote, LogOut } from 'lucide-react';
import { BaaSNotificationBell } from '@/components/baas/BaaSNotificationBell';
import { Button } from '@/components/ui/button';
import { BaaSSidebar } from '@/components/baas/BaaSSidebar';
import { BaaSRouteGuard } from '@/components/baas/BaaSRouteGuard';
import { useBaaSSessionTimeout } from '@/hooks/useBaaSSessionTimeout';
import SessionTimeoutCountdown from '@/components/SessionTimeoutCountdown';
import { PortalAuthGuard, PortalLoadingSkeleton } from '@/components/portal/PortalAuthGuard';

export default function BaaSLayout() {
  const { user, baasUser, loading, baasUserLoaded, signOut } = useBaaSAuth();
  const { showCountdown, countdownSeconds, resetTimer } = useBaaSSessionTimeout();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate('/baas/login', { replace: true });
  }, [loading, user, navigate]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/baas/login', { replace: true });
  }, [signOut, navigate]);

  return (
    <PortalAuthGuard
      loading={loading}
      profileLoaded={baasUserLoaded}
      user={user}
      profile={baasUser}
      onSignOut={handleSignOut}
      brandIcon={<Banknote className="w-10 h-10 text-accent" />}
      brandLabel="BaaS portal"
      accessDeniedMessage="Your account does not have BaaS Partner Portal access."
      retryWaitedMessage="Unable to load your BaaS partner profile. Please try again or contact your relationship manager."
    >
      {showCountdown && (
        <SessionTimeoutCountdown
          secondsLeft={countdownSeconds}
          onStayLoggedIn={resetTimer}
          portalName="BaaS Partner"
        />
      )}
      <div className="min-h-screen bg-background flex flex-col">
        <header className="h-14 border-b border-border/60 bg-card/80 backdrop-blur-xl flex items-center px-4 gap-3 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-accent shadow-glow-soft flex items-center justify-center">
              <Banknote className="w-4 h-4 text-accent-foreground" />
            </div>
            <span className="font-display text-sm font-bold text-foreground tracking-tight">BaaS Partner</span>
          </div>
          <div className="flex-1" />
          {baasUser && <span className="text-xs text-muted-foreground hidden sm:block font-medium">{baasUser.partnerName}</span>}
          <BaaSNotificationBell />
          <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-muted-foreground">
            <LogOut className="w-4 h-4" />
          </Button>
        </header>
        <div className="flex flex-1 overflow-hidden">
          <BaaSSidebar />
          <main className="flex-1 overflow-auto">
            {baasUser ? (
              <BaaSRouteGuard>
                <Suspense fallback={<PortalLoadingSkeleton />}>
                  <div className="p-6">
                    <Outlet />
                  </div>
                </Suspense>
              </BaaSRouteGuard>
            ) : (
              <PortalLoadingSkeleton />
            )}
          </main>
        </div>
      </div>
    </PortalAuthGuard>
  );
}
